import { useRef, useState } from "react";
import { Upload, X, Star } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUploader({ images, primary, onChange, onPrimaryChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleFiles = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        const next = [...images];
        for (const file of Array.from(files)) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/admin/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                next.push(data.url);
            } catch (e) {
                toast.error(`Upload failed: ${file.name}`);
            }
        }
        onChange(next);
        if (!primary && next.length) onPrimaryChange?.(next[0]);
        setUploading(false);
    };

    const remove = (url) => {
        const next = images.filter((i) => i !== url);
        onChange(next);
        if (primary === url) {
            onPrimaryChange?.(next[0] || "");
        }
    };

    return (
        <div data-testid="image-uploader">
            <div className="flex items-center gap-3 flex-wrap">
                {images.map((url) => (
                    <div
                        key={url}
                        className={`relative w-24 h-24 border rounded-sm overflow-hidden bg-[#0A0A0B] group ${
                            primary === url ? "border-[#00BCE5]" : "border-[#27272A]"
                        }`}
                    >
                        <img src={imageUrl(url)} alt="" className="w-full h-full object-cover" />
                        <button
                            type="button"
                            className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-sm opacity-90 hover:bg-[#FF3B30]"
                            onClick={() => remove(url)}
                            aria-label="Remove"
                            data-testid="image-remove"
                        >
                            <X size={12} />
                        </button>
                        <button
                            type="button"
                            className={`absolute bottom-1 left-1 px-1.5 py-0.5 text-[10px] font-display uppercase tracking-widest rounded-sm ${
                                primary === url ? "bg-[#00BCE5] text-black" : "bg-black/60 text-white"
                            }`}
                            onClick={() => onPrimaryChange?.(url)}
                            data-testid="image-set-primary"
                        >
                            <span className="inline-flex items-center gap-1">
                                <Star size={10} /> {primary === url ? "Primary" : "Set"}
                            </span>
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="w-24 h-24 border border-dashed border-[#27272A] rounded-sm flex flex-col items-center justify-center text-white/55 hover:border-[#00BCE5] hover:text-[#00BCE5]"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid="image-upload-trigger"
                >
                    <Upload size={18} />
                    <span className="text-[10px] uppercase tracking-widest font-display mt-1">
                        {uploading ? "Uploading…" : "Upload"}
                    </span>
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </div>
            <div className="text-xs text-white/45 mt-2">
                Click an image to set it as primary. JPG/PNG/WebP up to 6MB each.
            </div>
        </div>
    );
}
